import React, { useEffect, useRef, useState } from "react";

import { sanitizeHtml } from "../utils/sanitizeHtml";
import { useTranslation } from "react-i18next";

// Word-like (WYSIWYG) rich text editor. Users format text visually; the
// component still reads/writes an HTML string via value/onChange.
// An optional "HTML" mode lets advanced users edit the raw source.
const HtmlEditor = ({ value, onChange, rows = 6, placeholder }) => {
  const { t } = useTranslation();
  const [sourceMode, setSourceMode] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);
  const editorRef = useRef(null);
  const lastHtml = useRef(null); // last HTML we emitted, to avoid caret jumps
  const savedRange = useRef(null);

  const minHeight = rows * 28;

  // Sync external value into the editable area (initial load, edit another
  // record, form reset) without clobbering the caret while the user types.
  useEffect(() => {
    const el = editorRef.current;
    if (!el || sourceMode) return;
    const incoming = value || "";
    if (incoming !== lastHtml.current) {
      el.innerHTML = sanitizeHtml(incoming);
      lastHtml.current = incoming;
    }
    setIsEmpty(!el.textContent.trim() && !el.querySelector("img,video,iframe"));
  }, [value, sourceMode]);

  const emit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML === "<br>" ? "" : el.innerHTML;
    lastHtml.current = html;
    setIsEmpty(!el.textContent.trim() && !el.querySelector("img,video,iframe"));
    if (onChange) onChange(html);
  };

  // Remember the selection so prompts (link/image) don't lose it.
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current && editorRef.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (cmd, arg = null) => {
    restoreSelection();
    // Emit inline styles (<span style="color:...">) instead of <font> tags,
    // which the sanitizer would strip on display.
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(cmd, false, arg);
    saveSelection();
    emit();
  };

  // ---- Tables -------------------------------------------------------------
  const CELL_STYLE = "border:1px solid #ccc;padding:6px 8px;min-width:40px;vertical-align:top;";
  const TABLE_STYLE = "border-collapse:collapse;width:100%;margin:8px 0;";
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const [inTable, setInTable] = useState(false);

  const insertTable = (rowsN, colsN) => {
    setShowTable(false);
    setHover({ r: 0, c: 0 });
    const head = "<tr>" + Array.from({ length: colsN }, () => `<th style="${CELL_STYLE}background:#f5f5f5;"><br></th>`).join("") + "</tr>";
    const body = Array.from({ length: Math.max(rowsN - 1, 0) }, () =>
      "<tr>" + Array.from({ length: colsN }, () => `<td style="${CELL_STYLE}"><br></td>`).join("") + "</tr>"
    ).join("");
    insertHtml(`<table style="${TABLE_STYLE}"><thead>${head}</thead><tbody>${body}</tbody></table><p><br></p>`);
  };

  // Cell containing the caret, if any.
  const currentCell = () => {
    const sel = window.getSelection();
    let n = sel && sel.rangeCount ? sel.anchorNode : null;
    if (!n && savedRange.current) n = savedRange.current.startContainer;
    while (n && n !== editorRef.current) {
      if (n.nodeName === "TD" || n.nodeName === "TH") return n;
      n = n.parentNode;
    }
    return null;
  };

  const updateInTable = () => setInTable(!!currentCell());

  const makeCell = (tag) => {
    const c = document.createElement(tag);
    c.setAttribute("style", CELL_STYLE + (tag === "th" ? "background:#f5f5f5;" : ""));
    c.innerHTML = "<br>";
    return c;
  };

  const tableOp = (op) => {
    restoreSelection();
    const cell = currentCell();
    if (!cell) return;
    const row = cell.parentNode;
    const table = cell.closest("table");
    const idx = Array.from(row.children).indexOf(cell);
    if (op === "rowAfter" || op === "rowBefore") {
      const tr = document.createElement("tr");
      for (let i = 0; i < row.children.length; i++) tr.appendChild(makeCell("td"));
      if (row.parentNode.nodeName === "THEAD") {
        // New rows from the header go into the body.
        const tbody = table.tBodies[0] || table.appendChild(document.createElement("tbody"));
        tbody.insertBefore(tr, tbody.firstChild);
      } else {
        row.parentNode.insertBefore(tr, op === "rowAfter" ? row.nextSibling : row);
      }
    } else if (op === "colAfter" || op === "colBefore") {
      Array.from(table.rows).forEach((r) => {
        const ref = r.children[idx];
        const c = makeCell(r.parentNode.nodeName === "THEAD" ? "th" : "td");
        r.insertBefore(c, op === "colAfter" ? (ref ? ref.nextSibling : null) : ref);
      });
    } else if (op === "delRow") {
      if (table.rows.length <= 1) table.remove();
      else row.remove();
    } else if (op === "delCol") {
      if (row.children.length <= 1) table.remove();
      else Array.from(table.rows).forEach((r) => r.children[idx] && r.children[idx].remove());
    } else if (op === "delTable") {
      table.remove();
    }
    updateInTable();
    emit();
  };

  const COLORS = [
    "#000000", "#444444", "#888888", "#d0021b", "#f5a623", "#f8e71c",
    "#7ed321", "#417505", "#4a90e2", "#0b3d91", "#9013fe", "#bd10e0",
  ];
  const SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40];
  const [showColors, setShowColors] = useState(false);
  const [lastColor, setLastColor] = useState("#d0021b");

  const applyColor = (c) => {
    setLastColor(c);
    setShowColors(false);
    exec("foreColor", c);
  };

  // Apply an exact pixel font size to the selection. execCommand only knows
  // sizes 1-7, so mark with size 7 and convert those marks to styled spans.
  const applySize = (px) => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");
    el.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      // Drop nested sizes so the new size wins.
      f.querySelectorAll("span").forEach((n) => (n.style.fontSize = ""));
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    el.querySelectorAll('span[style*="xxx-large"]').forEach((n) => (n.style.fontSize = `${px}px`));
    saveSelection();
    emit();
  };

  const insertHtml = (html) => exec("insertHTML", html);

  const insertLink = () => {
    saveSelection();
    const url = window.prompt(t("htmlEditor.linkPrompt"), "https://");
    if (!url) return;
    const sel = savedRange.current;
    if (sel && !sel.collapsed) {
      exec("createLink", url);
      // Open links in a new tab.
      editorRef.current.querySelectorAll(`a[href="${CSS.escape(url)}"]`).forEach((a) => a.setAttribute("target", "_blank"));
      emit();
    } else {
      const esc = url.replace(/"/g, "&quot;");
      insertHtml(`<a href="${esc}" target="_blank">${esc}</a>&nbsp;`);
    }
  };

  const insertImage = () => {
    saveSelection();
    const url = window.prompt(t("htmlEditor.imagePrompt"), "https://");
    if (!url) return;
    const alt = (window.prompt(t("htmlEditor.imageAltPrompt"), "") || "").replace(/"/g, "&quot;");
    insertHtml(`<img src="${url.replace(/"/g, "&quot;")}" alt="${alt}" style="max-width:100%;height:auto;" />`);
  };

  // Convert common YouTube/Vimeo page URLs into an embeddable URL, else null.
  const toEmbedUrl = (raw) => {
    try {
      const url = new URL(raw);
      const host = url.hostname.replace(/^www\./, "");
      if (host === "youtu.be") {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      if (host === "youtube.com" || host === "youtube-nocookie.com") {
        if (url.pathname.startsWith("/embed/")) return `https://www.youtube.com${url.pathname}`;
        const id = url.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      if (host === "vimeo.com") {
        const id = url.pathname.split("/").filter(Boolean)[0];
        return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
      }
      if (host === "player.vimeo.com") return raw;
    } catch (e) {
      return null;
    }
    return null;
  };

  const insertVideo = () => {
    saveSelection();
    const url = window.prompt(t("htmlEditor.videoPrompt"), "https://");
    if (!url) return;
    const embed = toEmbedUrl(url.trim());
    if (embed) {
      insertHtml(
        `<iframe src="${embed}" width="560" height="315" style="max-width:100%;" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe><p><br></p>`
      );
    } else {
      insertHtml(`<video src="${url.trim().replace(/"/g, "&quot;")}" controls style="max-width:100%;height:auto;"></video><p><br></p>`);
    }
  };

  // Paste: keep formatting but sanitize it (Word / web pages bring junk).
  const handlePaste = (e) => {
    const html = e.clipboardData && e.clipboardData.getData("text/html");
    if (!html) return; // plain text: let the browser handle it
    e.preventDefault();
    const clean = sanitizeHtml(
      html
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/\s(class|lang)="[^"]*"/gi, "")
        .replace(/<\/?o:p>/gi, "")
    );
    document.execCommand("insertHTML", false, clean);
    emit();
  };

  const toggleSource = () => {
    if (sourceMode) {
      // Back to visual: force re-render of the value into the editor.
      lastHtml.current = null;
    }
    setSourceMode((s) => !s);
  };

  // onMouseDown preventDefault keeps the editor selection when clicking toolbar.
  const Btn = ({ title, onClick, children, style }) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      title={title}
      style={{ minWidth: 32, ...style }}
      disabled={sourceMode}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );

  const Sep = () => <span className="border-start mx-1" style={{ height: 22 }} />;

  return (
    <div className="border rounded bg-white">
      <div className="d-flex flex-wrap align-items-center gap-1 p-2 border-bottom bg-light">
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          title={t("htmlEditor.style", "Text style")}
          disabled={sourceMode}
          defaultValue=""
          onMouseDown={saveSelection}
          onChange={(e) => {
            if (e.target.value) exec("formatBlock", e.target.value);
            e.target.value = "";
          }}
        >
          <option value="" disabled>{t("htmlEditor.style", "Text style")}</option>
          <option value="<p>">{t("htmlEditor.paragraph")}</option>
          <option value="<h2>">{t("htmlEditor.heading")} 1</option>
          <option value="<h3>">{t("htmlEditor.heading")} 2</option>
          <option value="<h4>">{t("htmlEditor.heading")} 3</option>
          <option value="<blockquote>">{t("htmlEditor.quote", "Quote")}</option>
        </select>
        <Sep />
        <Btn title={t("htmlEditor.bold")} onClick={() => exec("bold")} style={{ fontWeight: "bold" }}>B</Btn>
        <Btn title={t("htmlEditor.italic")} onClick={() => exec("italic")} style={{ fontStyle: "italic" }}>I</Btn>
        <Btn title={t("htmlEditor.underline")} onClick={() => exec("underline")} style={{ textDecoration: "underline" }}>U</Btn>
        <Btn title={t("htmlEditor.strike", "Strikethrough")} onClick={() => exec("strikeThrough")} style={{ textDecoration: "line-through" }}>S</Btn>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          title={t("htmlEditor.size", "Text size")}
          disabled={sourceMode}
          defaultValue=""
          onMouseDown={saveSelection}
          onChange={(e) => {
            if (e.target.value) applySize(Number(e.target.value));
            e.target.value = "";
          }}
        >
          <option value="" disabled>{t("htmlEditor.size", "Text size")}</option>
          {SIZES.map((px) => (
            <option key={px} value={px}>{px}px</option>
          ))}
        </select>
        <div className="position-relative">
          <div className="btn-group">
            <Btn title={t("htmlEditor.color", "Text color")} onClick={() => applyColor(lastColor)}>
              <span style={{ fontWeight: "bold", borderBottom: `3px solid ${lastColor}` }}>A</span>
            </Btn>
            <Btn title={t("htmlEditor.moreColors", "More colors")} onClick={() => { saveSelection(); setShowColors((v) => !v); }} style={{ minWidth: 20 }}>▾</Btn>
          </div>
          {showColors && !sourceMode && (
            <div
              className="position-absolute bg-white border rounded shadow-sm p-2"
              style={{ zIndex: 20, top: "100%", left: 0, width: 168 }}
              onMouseDown={(e) => e.target.type !== "color" && e.preventDefault()}
            >
              <div className="d-flex flex-wrap gap-1 mb-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => applyColor(c)}
                    style={{ width: 22, height: 22, background: c, border: c === lastColor ? "2px solid #333" : "1px solid #ccc", borderRadius: 3, padding: 0 }}
                  />
                ))}
              </div>
              <label className="d-flex align-items-center gap-2 small mb-0" style={{ cursor: "pointer" }}>
                <input type="color" value={lastColor} onChange={(e) => applyColor(e.target.value)} />
                {t("htmlEditor.customColor", "Custom…")}
              </label>
            </div>
          )}
        </div>
        <Sep />
        <Btn title={t("htmlEditor.list")} onClick={() => exec("insertUnorderedList")}>• ≡</Btn>
        <Btn title={t("htmlEditor.numberedList", "Numbered list")} onClick={() => exec("insertOrderedList")}>1. ≡</Btn>
        <Btn title={t("htmlEditor.alignLeft", "Align left")} onClick={() => exec("justifyLeft")}>⯇-</Btn>
        <Btn title={t("htmlEditor.alignCenter", "Center")} onClick={() => exec("justifyCenter")}>⯇</Btn>
        <Btn title={t("htmlEditor.alignRight", "Align right")} onClick={() => exec("justifyRight")}>-⯈</Btn>
        <Sep />
        <Btn title={t("htmlEditor.link")} onClick={insertLink}>🔗</Btn>
        <Btn title={t("htmlEditor.image")} onClick={insertImage}>🖼</Btn>
        <Btn title={t("htmlEditor.video")} onClick={insertVideo}>🎥</Btn>
        <div className="position-relative">
          <Btn title={t("htmlEditor.table", "Insert table")} onClick={() => { saveSelection(); setShowTable((v) => !v); }}>▦</Btn>
          {showTable && !sourceMode && (
            <div
              className="position-absolute bg-white border rounded shadow-sm p-2"
              style={{ zIndex: 20, top: "100%", left: 0 }}
              onMouseDown={(e) => e.preventDefault()}
              onMouseLeave={() => setHover({ r: 0, c: 0 })}
            >
              <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 18px)", gap: 2 }}>
                {Array.from({ length: 64 }, (_, i) => {
                  const r = Math.floor(i / 8) + 1;
                  const c = (i % 8) + 1;
                  const on = r <= hover.r && c <= hover.c;
                  return (
                    <div
                      key={i}
                      onMouseEnter={() => setHover({ r, c })}
                      onClick={() => insertTable(r, c)}
                      style={{ width: 18, height: 18, cursor: "pointer", border: "1px solid " + (on ? "#4a90e2" : "#ccc"), background: on ? "#d6e6fb" : "#fff" }}
                    />
                  );
                })}
              </div>
              <div className="small text-muted text-center mt-1">
                {hover.r ? `${hover.c} × ${hover.r}` : t("htmlEditor.tablePick", "Pick size")}
              </div>
            </div>
          )}
        </div>
        <Sep />
        <Btn title={t("htmlEditor.undo", "Undo")} onClick={() => exec("undo")}>↶</Btn>
        <Btn title={t("htmlEditor.redo", "Redo")} onClick={() => exec("redo")}>↷</Btn>
        <Btn title={t("htmlEditor.clearFormat", "Clear formatting")} onClick={() => exec("removeFormat")}>⌫</Btn>
        <div className="ms-auto">
          <button
            type="button"
            className={"btn btn-sm " + (sourceMode ? "btn-secondary" : "btn-outline-secondary")}
            title={t("htmlEditor.sourceHint", "Edit raw HTML (advanced)")}
            onClick={toggleSource}
          >
            {sourceMode ? t("htmlEditor.visual", "Visual") : "</> HTML"}
          </button>
        </div>
      </div>

      {inTable && !sourceMode && (
        <div className="d-flex flex-wrap align-items-center gap-1 px-2 py-1 border-bottom small" style={{ background: "#f0f6ff" }}>
          <span className="text-muted me-1">▦ {t("htmlEditor.tableTools", "Table")}:</span>
          {[
            ["rowBefore", t("htmlEditor.rowAbove", "+ Row above")],
            ["rowAfter", t("htmlEditor.rowBelow", "+ Row below")],
            ["colBefore", t("htmlEditor.colLeft", "+ Column left")],
            ["colAfter", t("htmlEditor.colRight", "+ Column right")],
          ].map(([op, label]) => (
            <button key={op} type="button" className="btn btn-sm btn-outline-primary py-0" onMouseDown={(e) => e.preventDefault()} onClick={() => tableOp(op)}>
              {label}
            </button>
          ))}
          {[
            ["delRow", t("htmlEditor.delRow", "Delete row")],
            ["delCol", t("htmlEditor.delCol", "Delete column")],
            ["delTable", t("htmlEditor.delTable", "Delete table")],
          ].map(([op, label]) => (
            <button key={op} type="button" className="btn btn-sm btn-outline-danger py-0" onMouseDown={(e) => e.preventDefault()} onClick={() => tableOp(op)}>
              {label}
            </button>
          ))}
        </div>
      )}

      {sourceMode ? (
        <textarea
          className="form-control border-0"
          style={{ resize: "vertical", fontFamily: "monospace", fontSize: "0.9rem", borderRadius: 0, minHeight }}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange && onChange(e.target.value)}
        />
      ) : (
        <div className="position-relative">
          {isEmpty && placeholder && (
            <div className="position-absolute text-muted p-3" style={{ pointerEvents: "none" }}>
              {placeholder}
            </div>
          )}
          <div
            ref={editorRef}
            className="p-3 html-preview"
            contentEditable
            suppressContentEditableWarning
            style={{ minHeight, maxHeight: 600, overflowY: "auto", outline: "none" }}
            onInput={emit}
            onBlur={() => {
              saveSelection();
              emit();
            }}
            onKeyUp={() => { saveSelection(); updateInTable(); }}
            onMouseUp={() => { saveSelection(); updateInTable(); }}
            onPaste={handlePaste}
          />
        </div>
      )}
    </div>
  );
};

export default HtmlEditor;
